/* Brutalist Tenant Layout — strict grid, monospace chrome, signal-amber rail.

   What makes this different from Editorial:
   • Header is a single thin horizontal rail with three monospace cells
     ([STUDIO] / [INDEX/PORTFOLIO/SERVICES/CONTACT] / [BOOK_NOW →]) — no
     centered logo, no italics, no padding-heavy reveal-on-scroll trick.
   • Active nav state uses a hard underline at the signal-amber accent
     instead of opacity. Caps lock + monospace tracking gives every
     character a "technical specification" feel.
   • The footer mirrors the same column logic — three cells, hairline
     dividers between, no flowery serif. Issue/copyright info reads like
     a documentation footer.
   • Body always uses monospace for ALL UI chrome; serif (which here
     resolves to JetBrains Mono via the template's typeface pair) is
     reserved for the page content set by the page components.
   • A persistent vertical rail down the right edge displays the section
     index (01 / 02 / 03 / 04) so the visitor always knows where they
     are in the deck — borrows the Swiss-design / Massimo Vignelli
     convention of numbered chapter indexes.
*/

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";

const NAV = [
  { to: "/",          label: "INDEX",     end: true,  code: "01" },
  { to: "/portfolio", label: "PORTFOLIO",             code: "02" },
  { to: "/services",  label: "SERVICES",              code: "03" },
  { to: "/contact",   label: "CONTACT",               code: "04" },
];

const MONO = "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace";

export default function BrutalistTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = (settings?.studio_name || "STUDIO").toUpperCase();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = new Date().getFullYear();
  // Active section number — used by the right-edge index rail.
  const activeIdx = NAV.findIndex((it) =>
    it.to === "/" ? location.pathname === tenantHref("/") : location.pathname.startsWith(tenantHref(it.to))
  );
  const activeCode = activeIdx >= 0 ? NAV[activeIdx].code : "00";

  return (
    <div
      className="min-h-screen antialiased"
      style={{
        fontFamily: MONO,
        background: "var(--tenant-bg, #0A0908)",
        color: "var(--tenant-fg, #F5F2EC)",
      }}
      data-template="brutalist"
    >
      {/* ── Header rail (always visible, no scroll-state transitions) ─── */}
      <header
        className="fixed top-0 inset-x-0 z-40 border-b"
        style={{
          background: "var(--tenant-bg, #0A0908)",
          borderBottomColor: "var(--tenant-accent)",
        }}
        data-testid="brutalist-tenant-nav"
      >
        <div className="grid grid-cols-[1fr_auto_1fr] items-center h-12 lg:h-14 text-[10px] uppercase tracking-[0.32em]">
          <Link
            to={tenantHref("/")}
            className="px-4 lg:px-6 h-full flex items-center hover:opacity-70 transition-opacity truncate"
            style={{ color: "var(--tenant-fg, #F5F2EC)", borderRight: "1px solid var(--tenant-accent)" }}
            data-testid="brutalist-tenant-wordmark"
          >
            <TenantWordmark logoUrl={settings?.logo_url} coverLogoUrl={settings?.cover_logo_url} tone="dark" studioName={studioName} imgClassName="h-6 w-auto">
              <span>[{studioName}]</span>
            </TenantWordmark>
          </Link>
          <nav className="hidden md:flex items-stretch" aria-label="Primary">
            {NAV.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`brutalist-tenant-nav-${it.label.toLowerCase()}`}
                className="px-4 lg:px-6 flex items-center transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? "var(--tenant-accent)" : "var(--tenant-fg-soft, rgba(245,242,236,0.55))",
                  borderBottom: isActive ? "2px solid var(--tenant-accent)" : "2px solid transparent",
                  marginBottom: "-1px", // overlap the header rule so the active underline aligns
                })}
              >
                <span className="hidden lg:inline" style={{ opacity: 0.5, marginRight: 8 }}>{it.code}</span>
                {it.label}
              </NavLink>
            ))}
          </nav>
          {/* Mobile-only hamburger — fills the empty middle grid cell on
              phones so the visitor still has a path to Index/Portfolio/
              Services/Contact. */}
          <div className="md:hidden flex items-center justify-center">
            <TenantMobileNav tone="dark" ctaLabel="Book now" studioName={studioName} />
          </div>
          <Link
            to={tenantHref("/booking")}
            className="px-4 lg:px-6 h-full flex items-center justify-end hover:opacity-90 transition-opacity"
            style={{
              background: "var(--tenant-accent)",
              color: "var(--tenant-bg, #0A0908)",
              borderLeft: "1px solid var(--tenant-accent)",
            }}
            data-testid="brutalist-tenant-book-cta"
          >
            BOOK_NOW →
          </Link>
        </div>
      </header>

      {/* ── Right-edge section index rail (desktop only) ──────────────── */}
      <aside
        className="hidden xl:flex fixed top-1/2 -translate-y-1/2 right-3 z-30 flex-col gap-3 text-[9px] uppercase tracking-[0.42em]"
        aria-hidden
      >
        {NAV.map((it) => (
          <div
            key={it.code}
            className="flex items-center gap-2"
            style={{
              color: it.code === activeCode ? "var(--tenant-accent)" : "var(--tenant-fg-mute, rgba(245,242,236,0.30))",
            }}
          >
            <span>{it.code}</span>
            <span className="h-px w-4" style={{ background: "currentColor", opacity: it.code === activeCode ? 1 : 0.4 }} />
          </div>
        ))}
      </aside>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <main key={location.pathname} className="pt-12 lg:pt-14">
        <Outlet />
      </main>

      {/* ── Footer rail ───────────────────────────────────────────────── */}
      <footer
        className="border-t mt-16"
        style={{
          background: "var(--tenant-bg, #0A0908)",
          color: "var(--tenant-fg-soft, rgba(245,242,236,0.55))",
          borderTopColor: "var(--tenant-accent)",
        }}
        data-testid="brutalist-tenant-footer"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 text-[10px] uppercase tracking-[0.32em]">
          <div className="px-4 lg:px-6 py-5 md:border-r" style={{ borderRightColor: "var(--tenant-accent)" }}>
            <div className="opacity-50">[STUDIO]</div>
            <div className="mt-2" style={{ color: "var(--tenant-fg)" }}>{studioName}</div>
            {settings?.tagline && (
              <div className="mt-1 normal-case tracking-normal text-xs" style={{ opacity: 0.6 }}>
                {settings.tagline}
              </div>
            )}
          </div>
          <div className="px-4 lg:px-6 py-5 md:border-r" style={{ borderRightColor: "var(--tenant-accent)" }}>
            <div className="opacity-50">[CONTACT]</div>
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="block mt-2 hover:opacity-100 normal-case tracking-normal text-xs" style={{ color: "var(--tenant-fg)", opacity: 0.85 }}>
                {settings.contact_email}
              </a>
            )}
            {settings?.contact_location && (
              <div className="mt-1 normal-case tracking-normal text-xs" style={{ opacity: 0.6 }}>
                {settings.contact_location}
              </div>
            )}
          </div>
          <div className="px-4 lg:px-6 py-5">
            <div className="opacity-50">[META]</div>
            <div className="mt-2 normal-case tracking-normal text-xs" style={{ opacity: 0.7 }}>
              © {year} {studioName}.{" "}
              <LumetierFooterCredit className="hover:opacity-100" testid="brutalist-lumetier-credit" />
            </div>
            <div className="mt-1 normal-case tracking-normal text-xs" style={{ opacity: 0.5 }}>
              Build {String(year).slice(-2)}.{String(new Date().getMonth() + 1).padStart(2, "0")}
            </div>
          </div>
        </div>
      </footer>

      <TenantSignature tone="dark" />
    </div>
  );
}
